import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { ReactComponent as CheckIcon } from '../../assets/icons/check.svg';

interface CheckBoxProps {
  label: string;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  required?: boolean;
  id?: string;
  name?: string;
  style?: React.CSSProperties;
  labelPosition?: 'right' | 'left';
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  checkboxWrapperReverse: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hiddenCheckbox: {
    opacity: 0,
    position: 'absolute',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    zIndex: 1,
    margin: 0,
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
  customCheckbox: {
    width: '20px',
    height: '20px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#666666',
    },
    '&.checked': {
      backgroundColor: 'var(--dai-primary-color)',
      borderColor: 'var(--dai-primary-color)',
    },
    '&.disabled': {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
    },
    '&.error': {
      borderColor: '#e53935',
    },
  },
  checkmark: {
    color: '#fff',
    fontWeight: 'bold',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '12px',
      height: '12px',
      fill: '#fff',
    },
    '&.visible': {
      opacity: 1,
    },
  },
  label: {
    fontSize: '14px',
    lineHeight: '1.4',
    userSelect: 'none',
    cursor: 'pointer',
    '&.disabled': {
      color: '#999',
      cursor: 'not-allowed',
    },
  },
  errorText: {
    color: '#e53935',
    fontSize: '12px',
    marginTop: '5px',
  },
  requiredMark: {
    color: '#e53935',
    marginLeft: '2px',
  },
});

// Omit the onChange from HTML input attributes to avoid type conflicts
type CheckBoxHTMLProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
>;

const CheckBox: React.FC<CheckBoxProps & CheckBoxHTMLProps> = ({
  label,
  checked = false,
  onChange,
  disabled = false,
  errorMessage,
  className = '',
  required = false,
  id,
  labelPosition = 'right',
  style,
  ...rest
}) => {
  const classes = useStyles();
  const [isChecked, setIsChecked] = useState(checked);
  const [showError, setShowError] = useState(!!errorMessage);
  const checkboxId =
    id || `checkbox-${Math.random().toString(36).substring(2, 9)}`;

  // Sync with parent component's state
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  useEffect(() => {
    setShowError(!!errorMessage);
  }, [errorMessage]);

  const handleChange = (checked: boolean) => {
    if (!disabled) {
      onChange(checked);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      handleChange(!isChecked);
    }
  };

  const wrapperClass =
    labelPosition === 'right'
      ? classes.checkboxWrapper
      : classes.checkboxWrapperReverse;

  return (
    <div className={`${classes.container} ${className}`} style={style}>
      <div className={wrapperClass}>
        <div className={classes.inputContainer}>
          <input
            type="checkbox"
            id={checkboxId}
            checked={isChecked}
            onChange={(e) => handleChange(e.target.checked)}
            className={classes.hiddenCheckbox}
            disabled={disabled}
            required={required}
            {...rest}
          />
          <div
            className={`${classes.customCheckbox} ${
              isChecked ? 'checked' : ''
            } ${disabled ? 'disabled' : ''} ${showError ? 'error' : ''}`}
            onClick={handleClick}
          >
            <span
              className={`${classes.checkmark} ${isChecked ? 'visible' : ''}`}
            >
              <CheckIcon />
            </span>
          </div>
        </div>
        <label
          htmlFor={checkboxId}
          className={`${classes.label} ${disabled ? 'disabled' : ''}`}
          onClick={handleClick}
        >
          {label}
          {required && <span className={classes.requiredMark}>*</span>}
        </label>
      </div>
      {showError && <div className={classes.errorText}>{errorMessage}</div>}
    </div>
  );
};

export default CheckBox;
